import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { apiClient } from '../api/client'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'

// ─── Types ────────────────────────────────────────────────────────────────────

interface Step1Data {
  nombreEmpresa: string
  rucNit: string
  pais: string
}

interface Step2Data {
  codigo: string
  tipoMaquina: string
  marca: string
}

interface Step3Data {
  nombre: string
  ubicacion: string
  capacidadLitros: string
}

interface Step4Data {
  nombre: string
  ci: string
  telefono: string
  pinTemporal: string
}

// ─── Progress bar ─────────────────────────────────────────────────────────────

function ProgressBar({ step, total }: { step: number; total: number }) {
  const STEPS = ['Empresa', 'Vehículo', 'Tanque', 'Operador']
  return (
    <div className="w-full mb-8">
      <div className="flex items-center justify-between mb-3">
        {STEPS.map((label, i) => {
          const idx = i + 1
          const done = idx < step
          const active = idx === step
          return (
            <div key={label} className="flex flex-col items-center flex-1">
              <div
                className={[
                  'w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-colors',
                  done
                    ? 'bg-primary text-white'
                    : active
                    ? 'bg-primary text-white ring-4 ring-primary/20'
                    : 'bg-slate-200 dark:bg-slate-700 text-slate-500 dark:text-slate-400',
                ].join(' ')}
              >
                {done ? '✓' : idx}
              </div>
              <span
                className={`text-xs mt-1 font-medium ${
                  active
                    ? 'text-primary'
                    : done
                    ? 'text-slate-600 dark:text-slate-300'
                    : 'text-slate-400 dark:text-slate-500'
                }`}
              >
                {label}
              </span>
            </div>
          )
        })}
      </div>
      {/* connector line */}
      <div className="relative h-1 bg-slate-200 dark:bg-slate-700 rounded-full mt-1">
        <div
          className="absolute left-0 top-0 h-1 bg-primary rounded-full transition-all duration-300"
          style={{ width: `${((step - 1) / (total - 1)) * 100}%` }}
        />
      </div>
    </div>
  )
}

// ─── Step 1: Empresa ──────────────────────────────────────────────────────────

function Step1({
  data,
  onChange,
  error,
}: {
  data: Step1Data
  onChange: (d: Step1Data) => void
  error: string | null
}) {
  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
          Información de tu empresa
        </h2>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
          Configuremos tu cuenta en AuraFleet
        </p>
      </div>
      <Input
        label="Nombre de la empresa"
        placeholder="Ej. Constructora Tiahuanaco"
        value={data.nombreEmpresa}
        onChange={(e) => onChange({ ...data, nombreEmpresa: e.target.value })}
      />
      <Input
        label="RUC / NIT"
        placeholder="Ej. 1234567890"
        value={data.rucNit}
        onChange={(e) => onChange({ ...data, rucNit: e.target.value })}
      />
      <div className="flex flex-col gap-1">
        <label className="text-sm font-medium text-slate-700 dark:text-slate-300">País</label>
        <select
          value={data.pais}
          onChange={(e) => onChange({ ...data, pais: e.target.value })}
          className="w-full rounded-lg border border-slate-300 bg-white text-slate-900 text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary dark:bg-slate-800 dark:border-slate-600 dark:text-slate-100"
        >
          <option value="BO">Bolivia</option>
          <option value="AR">Argentina</option>
          <option value="BR">Brasil</option>
          <option value="CL">Chile</option>
          <option value="CO">Colombia</option>
          <option value="PE">Perú</option>
          <option value="PY">Paraguay</option>
          <option value="UY">Uruguay</option>
        </select>
      </div>
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
          <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
        </div>
      )}
    </div>
  )
}

// ─── Step 2: Vehículo ─────────────────────────────────────────────────────────

const TIPO_MAQUINA_OPTIONS = [
  { value: 'EXCAVADORA', label: 'Excavadora' },
  { value: 'VOLQUETA', label: 'Volqueta' },
  { value: 'RETROEXCAVADORA', label: 'Retroexcavadora' },
  { value: 'MOTONIVELADORA', label: 'Motoniveladora' },
  { value: 'COMPACTADORA', label: 'Compactadora' },
  { value: 'GRUA', label: 'Grúa' },
  { value: 'BULLDOZER', label: 'Bulldozer' },
  { value: 'CAMION', label: 'Camión' },
  { value: 'OTRO', label: 'Otro' },
]

function Step2({
  data,
  onChange,
  error,
}: {
  data: Step2Data
  onChange: (d: Step2Data) => void
  error: string | null
}) {
  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
          Primer vehículo
        </h2>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
          Añade la primera maquinaria de tu flota
        </p>
      </div>
      <Input
        label="Código del vehículo"
        placeholder="Ej. EXC-001"
        value={data.codigo}
        onChange={(e) => onChange({ ...data, codigo: e.target.value })}
      />
      <div className="flex flex-col gap-1">
        <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
          Tipo de maquinaria
        </label>
        <select
          value={data.tipoMaquina}
          onChange={(e) => onChange({ ...data, tipoMaquina: e.target.value })}
          className="w-full rounded-lg border border-slate-300 bg-white text-slate-900 text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary dark:bg-slate-800 dark:border-slate-600 dark:text-slate-100"
        >
          <option value="">Seleccionar tipo...</option>
          {TIPO_MAQUINA_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>
      <Input
        label="Marca"
        placeholder="Ej. Caterpillar, Komatsu, Volvo..."
        value={data.marca}
        onChange={(e) => onChange({ ...data, marca: e.target.value })}
      />
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
          <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
        </div>
      )}
    </div>
  )
}

// ─── Step 3: Tanque ───────────────────────────────────────────────────────────

function Step3({
  data,
  onChange,
  error,
}: {
  data: Step3Data
  onChange: (d: Step3Data) => void
  error: string | null
}) {
  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
          Primer tanque de combustible
        </h2>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
          Registra el depósito principal de tu operación
        </p>
      </div>
      <Input
        label="Nombre del tanque"
        placeholder="Ej. Tanque Principal Obra Norte"
        value={data.nombre}
        onChange={(e) => onChange({ ...data, nombre: e.target.value })}
      />
      <Input
        label="Ubicación"
        placeholder="Ej. Km 34 Carretera Norte"
        value={data.ubicacion}
        onChange={(e) => onChange({ ...data, ubicacion: e.target.value })}
      />
      <Input
        label="Capacidad (litros)"
        type="number"
        placeholder="Ej. 5000"
        min="0"
        value={data.capacidadLitros}
        onChange={(e) => onChange({ ...data, capacidadLitros: e.target.value })}
      />
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
          <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
        </div>
      )}
    </div>
  )
}

// ─── Step 4: Operador ─────────────────────────────────────────────────────────

function Step4({
  data,
  onChange,
  error,
}: {
  data: Step4Data
  onChange: (d: Step4Data) => void
  error: string | null
}) {
  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
          Invitar primer operador
        </h2>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
          El operador usará el PIN para registrar cargas desde la app móvil
        </p>
      </div>
      <Input
        label="Nombre completo"
        placeholder="Ej. Juan Pérez López"
        value={data.nombre}
        onChange={(e) => onChange({ ...data, nombre: e.target.value })}
      />
      <Input
        label="CI (Cédula de identidad)"
        placeholder="Ej. 12345678"
        value={data.ci}
        onChange={(e) => onChange({ ...data, ci: e.target.value })}
      />
      <Input
        label="Teléfono"
        type="tel"
        placeholder="Ej. 70012345"
        value={data.telefono}
        onChange={(e) => onChange({ ...data, telefono: e.target.value })}
      />
      <Input
        label="PIN temporal (6 dígitos)"
        type="number"
        placeholder="Ej. 123456"
        maxLength={6}
        value={data.pinTemporal}
        onChange={(e) => {
          const v = e.target.value.slice(0, 6)
          onChange({ ...data, pinTemporal: v })
        }}
      />
      <p className="text-xs text-slate-400 dark:text-slate-500">
        El operador debe cambiar este PIN al iniciar sesión por primera vez.
      </p>
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
          <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
        </div>
      )}
    </div>
  )
}

// ─── Main Wizard ──────────────────────────────────────────────────────────────

export default function Onboarding() {
  const navigate = useNavigate()
  const TOTAL_STEPS = 4

  const [step, setStep] = useState(1)
  const [loading, setLoading] = useState(false)
  const [stepError, setStepError] = useState<string | null>(null)

  const [step1, setStep1] = useState<Step1Data>({
    nombreEmpresa: '',
    rucNit: '',
    pais: 'BO',
  })

  const [step2, setStep2] = useState<Step2Data>({
    codigo: '',
    tipoMaquina: '',
    marca: '',
  })

  const [step3, setStep3] = useState<Step3Data>({
    nombre: '',
    ubicacion: '',
    capacidadLitros: '',
  })

  const [step4, setStep4] = useState<Step4Data>({
    nombre: '',
    ci: '',
    telefono: '',
    pinTemporal: '',
  })

  async function handleNext() {
    setStepError(null)
    setLoading(true)
    try {
      if (step === 1) {
        if (!step1.nombreEmpresa.trim()) throw new Error('El nombre de la empresa es requerido')
        await apiClient.post('/tenants', {
          nombre: step1.nombreEmpresa,
          rucNit: step1.rucNit,
          pais: step1.pais,
        })
      } else if (step === 2) {
        if (!step2.codigo.trim()) throw new Error('El código del vehículo es requerido')
        if (!step2.tipoMaquina) throw new Error('Selecciona el tipo de maquinaria')
        await apiClient.post('/vehiculos', {
          codigo: step2.codigo,
          tipoMaquina: step2.tipoMaquina,
          marca: step2.marca,
        })
      } else if (step === 3) {
        if (!step3.nombre.trim()) throw new Error('El nombre del tanque es requerido')
        const cap = Number(step3.capacidadLitros)
        if (!step3.capacidadLitros || isNaN(cap) || cap <= 0)
          throw new Error('La capacidad debe ser un número mayor a cero')
        await apiClient.post('/tanques', {
          nombre: step3.nombre,
          ubicacion: step3.ubicacion,
          capacidadLitros: cap,
        })
      }
      setStep((s) => s + 1)
    } catch (err: unknown) {
      const msg =
        err instanceof Error
          ? err.message
          : (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
            'Ocurrió un error inesperado'
      setStepError(msg)
    } finally {
      setLoading(false)
    }
  }

  async function handleFinish() {
    setStepError(null)
    setLoading(true)
    try {
      if (!step4.nombre.trim()) throw new Error('El nombre del operador es requerido')
      if (!step4.ci.trim()) throw new Error('El CI es requerido')
      if (step4.pinTemporal.length !== 6 || !/^\d{6}$/.test(step4.pinTemporal))
        throw new Error('El PIN debe tener exactamente 6 dígitos')
      await apiClient.post('/operadores', {
        nombre: step4.nombre,
        ci: step4.ci,
        telefono: step4.telefono,
        pinTemporal: step4.pinTemporal,
      })
      navigate('/')
    } catch (err: unknown) {
      const msg =
        err instanceof Error
          ? err.message
          : (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
            'Ocurrió un error inesperado'
      setStepError(msg)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo / brand */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-primary">AuraFleet</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Configura tu cuenta en minutos
          </p>
        </div>

        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm p-6">
          <ProgressBar step={step} total={TOTAL_STEPS} />

          {step === 1 && (
            <Step1 data={step1} onChange={setStep1} error={stepError} />
          )}
          {step === 2 && (
            <Step2 data={step2} onChange={setStep2} error={stepError} />
          )}
          {step === 3 && (
            <Step3 data={step3} onChange={setStep3} error={stepError} />
          )}
          {step === 4 && (
            <Step4 data={step4} onChange={setStep4} error={stepError} />
          )}

          {/* Navigation */}
          <div className="flex items-center justify-between mt-6 pt-4 border-t border-slate-100 dark:border-slate-700">
            {step > 1 ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setStep((s) => s - 1)
                  setStepError(null)
                }}
                disabled={loading}
              >
                ← Anterior
              </Button>
            ) : (
              <div />
            )}

            {step < TOTAL_STEPS ? (
              <Button
                variant="primary"
                size="md"
                loading={loading}
                onClick={handleNext}
              >
                Siguiente →
              </Button>
            ) : (
              <Button
                variant="primary"
                size="md"
                loading={loading}
                onClick={handleFinish}
              >
                Ir al Dashboard →
              </Button>
            )}
          </div>
        </div>

        <p className="text-center text-xs text-slate-400 mt-4">
          Paso {step} de {TOTAL_STEPS}
        </p>
      </div>
    </div>
  )
}
